"use client";

import { c as _c } from "react/compiler-runtime";
import { Copy01, Share03 } from "@untitledui/icons";
import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useClipboard } from "@/hooks/use-clipboard";
import { cx } from "@/utils/cx";
import { getUniswapPoolUrl } from "../utils/uniswap";
const CLOSE_DELAY_MS = 250;
/** Gap between trigger and overlay */
const GAP = 4;
/** Extra padding toward trigger - extends hover hit area into the gap (safe-area triangle / invisible bridge) */
const BRIDGE_PADDING = GAP + 8;
interface PoolAddressHoverCardContextValue {
  cancelClose: () => void;
  scheduleClose: () => void;
}
const PoolAddressHoverCardContext = createContext<PoolAddressHoverCardContextValue | null>(null);
interface PoolAddressHoverCardProps {
  poolAddress: string;
  network: string;
  children: React.ReactNode;
}
export function PoolAddressHoverCard({
  poolAddress,
  network,
  children
}: PoolAddressHoverCardProps) {
  const [isOpen, setIsOpen] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const triggerRef = useRef<HTMLDivElement>(null);
  const {
    copy,
    copied
  } = useClipboard();
  const cancelClose = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = undefined;
    }
  }, []);
  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => setIsOpen(false), CLOSE_DELAY_MS);
  }, [cancelClose]);
  const contextValue: PoolAddressHoverCardContextValue = {
    cancelClose,
    scheduleClose
  };
  const handleCopy = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    copy(poolAddress);
  }, [poolAddress, copy]);
  return <PoolAddressHoverCardContext.Provider value={contextValue}>
			<div ref={triggerRef} className="inline-flex" onMouseEnter={() => {
      cancelClose();
      setIsOpen(true);
    }} onMouseLeave={scheduleClose}>
				{children}
			</div>
			{isOpen && createPortal(<PoolAddressHoverCardOverlay poolAddress={poolAddress} network={network} copied={!!copied} onCopy={handleCopy} triggerRect={triggerRef.current?.getBoundingClientRect()} />, document.body)}
		</PoolAddressHoverCardContext.Provider>;
}
interface PoolAddressHoverCardOverlayProps {
  poolAddress: string;
  network: string;
  copied: boolean;
  onCopy: (e: React.MouseEvent) => void;
  triggerRect: DOMRect | undefined;
}
function PoolAddressHoverCardOverlay(t0) {
  const $ = _c(28);
  const {
    poolAddress,
    network,
    copied,
    onCopy,
    triggerRect
  } = t0;
  const ctx = useContext(PoolAddressHoverCardContext);
  let t1;
  if ($[0] !== network || $[1] !== poolAddress) {
    t1 = getUniswapPoolUrl(network, poolAddress);
    $[0] = network;
    $[1] = poolAddress;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const uniswapUrl = t1;
  if (!triggerRect) {
    return null;
  }
  const left = triggerRect.left - 4;
  const overlayAnchorY = triggerRect.top - GAP;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = cx("z-50 flex max-w-sm min-w-0 origin-top flex-col gap-2 rounded-lg bg-primary-solid px-3 py-3 shadow-lg", "duration-150 ease-out animate-in fade-in slide-in-from-bottom-1 zoom-in-95");
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== left || $[5] !== overlayAnchorY) {
    t3 = {
      position: "fixed",
      left,
      top: overlayAnchorY,
      transform: "translate(0, -100%)",
      paddingBottom: BRIDGE_PADDING,
      marginBottom: -BRIDGE_PADDING
    };
    $[4] = left;
    $[5] = overlayAnchorY;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = ctx?.cancelClose;
  const t5 = ctx?.scheduleClose;
  let t6;
  if ($[7] !== poolAddress) {
    t6 = <span className="min-w-0 font-mono text-xs font-medium break-all text-white" title={poolAddress}>{poolAddress}</span>;
    $[7] = poolAddress;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const t7 = copied ? "Copied" : "Copy address";
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Copy01 className="size-3.5" />;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== onCopy || $[11] !== t7) {
    t9 = <button type="button" onClick={onCopy} className="shrink-0 rounded p-1 text-tooltip-supporting-text outline-focus-ring hover:bg-white/10 hover:text-white" aria-label={t7}>{t8}</button>;
    $[10] = onCopy;
    $[11] = t7;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== t6 || $[14] !== t9) {
    t10 = <div className="flex items-start justify-between gap-2">{t6}{t9}</div>;
    $[13] = t6;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Share03 className="size-3.5" />;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== uniswapUrl) {
    t12 = <a href={uniswapUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs font-medium text-tooltip-supporting-text hover:text-white">{t11}View on Uniswap</a>;
    $[17] = uniswapUrl;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== copied) {
    t13 = copied && <span className="text-xs font-medium text-green-400">Copied!</span>;
    $[19] = copied;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== t10 || $[22] !== t12 || $[23] !== t13 || $[24] !== t3 || $[25] !== t4 || $[26] !== t5) {
    t14 = <div className={t2} style={t3} onMouseEnter={t4} onMouseLeave={t5}>{t10}{t12}{t13}</div>;
    $[21] = t10;
    $[22] = t12;
    $[23] = t13;
    $[24] = t3;
    $[25] = t4;
    $[26] = t5;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  return t14;
}